// src/components/automations/AutomationsHome.js
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Modal, Collapse, Table } from 'react-bootstrap';

import {
    Utils,
    fetchWithAuth,
    filtersObjectHasAnyValue,
    findOptionByValue,
    AUTOMATION_STATUS_CODES,
    getDefaultPageSize,
    formatDateTime,
    getTotalPages,
    canAny,
    exportListCsv,
    getBrowserTimeZone,
} from '../../utils';
import { useActionDropdown } from '../../hooks/useActionDropdown';
import { useTableMaxHeight } from '../../hooks/useTableMaxHeight';
import { useScrollEndDetection } from '../../hooks/useScrollEndDetection';
import {
    MdKeyboardArrowDown,
    MdAddCircle,
    MdFileDownload,
    MdEdit,
    MdDeleteOutline,
    MdOutlineCheckCircle,
    MdOutlineRemoveCircleOutline,
    MdOutlineRemoveRedEye,
} from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import { BsPencil, BsClockHistory, BsThreeDotsVertical } from 'react-icons/bs';
import { LuCopy } from 'react-icons/lu';
import ListPaginationBar from '../common/ListPaginationBar';
import AutomationsFiltersBar from './AutomationsFiltersBar';
import { duplicateAutomationAction, deleteAutomationAction, changeAutomationStatusAction, setAutomationLiveAction } from './AutomationActions';
import AutomationAuditLogsModal from './AutomationAuditLogsModal';


const EMPTY_AUTOMATION_FILTERS = { name: '', status: '', createdBy: '', automationId: '', dateFrom: '', dateTo: '', datePreset: '' };

const AutomationsHome = () => {
    const params = useParams();
    const navigate = useNavigate();
    const appId = params.appId;
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewModal, setShowNewModal] = useState(false);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_AUTOMATION_FILTERS }));
    const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_AUTOMATION_FILTERS }));
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = getDefaultPageSize();
    const [automationsTotal, setAutomationsTotal] = useState(0);
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedAutomation, setSelectedAutomation] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    const getAutomationActionOptionCount = (item) => {
        const status = Number(item?.status);
        let count = 0;
        if (canAny(["automation.edit"])) count += 3; // Edit/View, Rename, Duplicate
        if (canAny(["automation.view"])) count += 1; // View Audit Logs
        if (canAny(["automation.change_status"]) && [1, 2, 3, 4].includes(status)) count += 1; // Set Live / Pause
        if (canAny(["automation.delete"])) count += 1; // Delete
        return count || 1;
    };
    const { openActionItem, dropdownPosition, dropdownRef, closeDropdown, handleOpenDropdown } = useActionDropdown({
        tableScrollRef,
        optionCount: getAutomationActionOptionCount,
        menuWidth: 200,
    });
    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows?.length);

    const fetchAutomations = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'automations/list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    app_id: appId,
                    name: appliedFilters.name !== '' && appliedFilters.name != null
                        ? appliedFilters.name
                        : undefined,
                    status: appliedFilters.status !== '' && appliedFilters.status != null
                        ? appliedFilters.status
                        : undefined,
                    ...(appliedFilters.dateFrom && appliedFilters.dateTo
                        ? {
                            date_from: appliedFilters.dateFrom,
                            date_to: appliedFilters.dateTo,
                            timezone: getBrowserTimeZone() || undefined,
                        }
                        : {}),
                    page: currentPage,
                    pageSize: pageSize,
                }),
            })
            const result = await response.json();
            if (result.status) {
                setRows(result.data || []);
                setAutomationsTotal(result.meta?.total ?? (result.data?.length || 0));
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching automations:', error);
            setLoading(false);
        }
    }


    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv('automations/list', {
                app_id: appId,
                name: appliedFilters.name !== '' && appliedFilters.name != null ? appliedFilters.name : undefined,
                status: appliedFilters.status !== '' && appliedFilters.status != null ? appliedFilters.status : undefined,
                ...(appliedFilters.dateFrom && appliedFilters.dateTo
                    ? {
                        date_from: appliedFilters.dateFrom,
                        date_to: appliedFilters.dateTo,
                        timezone: getBrowserTimeZone() || undefined,
                    } : {}),
            }, "automations.csv");
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        fetchAutomations();
    }, [appId, appliedFilters, currentPage]);


    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_AUTOMATION_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
    };

    const totalRows = automationsTotal || 0;
    const totalPages = getTotalPages(totalRows, pageSize);
    const safePage = Math.min(Math.max(1, currentPage), totalPages);


    const handleOpenAutomation = (automation) => {
        if (!automation?.id) return;
        navigate(`/apps/${appId}/automations/${automation.id}`);
    };

    // Live -> Pause. Anything else (Draft / Scheduled / Paused) -> Set Live, which runs the same
    // pre-launch checks as the builder and, if something is wrong, offers to open the automation.
    const handleToggleStatus = async (automation) => {
        if (!automation || statusUpdating) return;
        const liveValue = findOptionByValue(AUTOMATION_STATUS_CODES, 3)?.value;
        const pausedValue = findOptionByValue(AUTOMATION_STATUS_CODES, 4)?.value;
        setStatusUpdating(true);
        try {
            const result = Number(automation.status) === liveValue
                ? await changeAutomationStatusAction(appId, automation.id, pausedValue)
                : await setAutomationLiveAction(appId, automation, handleOpenAutomation);
            if (result) fetchAutomations();
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleDeleteAutomation = async (automation) => {
        if (!automation || deleting) return;
        setDeleting(true);
        const ok = await deleteAutomationAction(appId, automation);
        if (ok) fetchAutomations();
        setDeleting(false);
    };

    const handleOpenRenameModal = (automation) => {
        if (!automation) return;
        setSelectedAutomation(automation);
        setRenameValue(automation.name || '');
        setShowRenameModal(true);
    };

    const handleRenameAutomation = async () => {
        if (!selectedAutomation || !renameValue.trim() || renaming) return;
        setRenaming(true);
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'automations/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ app_id: appId, id: selectedAutomation.id, name: renameValue.trim() }),
            });
            const result = await response.json();
            if (result.status) {
                toast.success('Automation renamed');
                setShowRenameModal(false);
                setSelectedAutomation(null);
                fetchAutomations();
            } else {
                toast.error(result.message || 'Could not rename automation');
            }
        } catch (error) {
            console.error('Error renaming automation:', error);
            toast.error('Could not rename automation');
        } finally {
            setRenaming(false);
        }
    };

    const handleDuplicateAutomation = async (automation) => {
        if (!automation || duplicating) return;
        setDuplicating(true);
        const clone = await duplicateAutomationAction(appId, automation);
        if (clone) {
            fetchAutomations();
            const openEdit = await Swal.fire({
                title: "Automation duplicated",
                text: `"${clone.name || 'Copy'}" has been created as a draft. Open it in the editor now?`,
                icon: "success",
                showCancelButton: true,
                confirmButtonColor: "#4c5fd5",
                cancelButtonColor: "#6c757d",
                confirmButtonText: "Open Editor",
                cancelButtonText: "Not Now",
            });
            if (openEdit.isConfirmed && clone.id) {
                navigate(`/apps/${appId}/automations/${clone.id}`);
            }
        }
        setDuplicating(false);
    };

    const handleOpenAuditLogs = async (automation) => {
        if (!automation) return;
        setSelectedAutomation(automation);
        setShowAuditModal(true);
        setAuditLoading(true);
        try {
            const response = await fetchWithAuth(Utils.baseurl + 'automations/audit-logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ app_id: appId, id: automation.id, page: 1, pageSize: 50 }),
            });
            const result = await response.json();
            if (result.status) {
                setAuditLogs(result.data || []);
            } else {
                toast.error(result.message || 'Could not load audit logs');
            }
        } catch (error) {
            console.error('Error loading audit logs:', error);
            toast.error('Could not load audit logs');
        } finally {
            setAuditLoading(false);
        }
    };

    function handleCreate() {
        if (!newName.trim()) return;
        setCreating(true);
        fetchWithAuth(Utils.baseurl + 'automations/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                app_id: appId,
                name: newName.trim(),
                status: appliedFilters.status !== '' && appliedFilters.status != null
                    ? appliedFilters.status
                    : undefined,
                ...(appliedFilters.dateFrom && appliedFilters.dateTo
                    ? {
                        date_from: appliedFilters.dateFrom,
                        date_to: appliedFilters.dateTo,
                        timezone: getBrowserTimeZone() || undefined,
                    }
                    : {}),
                page: currentPage,
                pageSize,
                template: 0
            }),
        })
            .then((r) => r.json())
            .then((result) => {
                if (result.status) {
                    navigate(`/apps/${appId}/automations/${result.data.id}`);
                } else {
                    toast.error(result.message);
                }
            })
            .catch((e) => console.error(e))
            .finally(() => setCreating(false));
    }

    if (loading) return <div style={{ padding: 24 }}>Loading...</div>;


    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>Automations</h1>
                    </div>
                    <div className="add_app" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => { setLoading(true); fetchAutomations(); }}>
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                        {canAny(["automation.add"]) ? (
                            <button type="button" className="add_app_btn btn_theme_primary" onClick={() => setShowNewModal(true)}>
                                <MdAddCircle />
                                <span>New Automation</span>
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className="list-page-filter-card">
                    <button
                        type="button"
                        className="list-page-filter-accordion-header"
                        onClick={() => setFilterAccordionOpen((o) => !o)}
                        aria-expanded={filterAccordionOpen}
                        aria-controls="automations-filter-panel"
                        id="automations-filter-accordion"
                    >
                        <span className="list-page-filter-accordion-title">Filter</span>
                        <MdKeyboardArrowDown
                            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                            size={22}
                            aria-hidden
                        />
                    </button>
                    <Collapse in={filterAccordionOpen}>
                        <div
                            className="list-page-filter-accordion-body"
                            id="automations-filter-panel"
                            role="region"
                            aria-labelledby="automations-filter-accordion"
                        >
                            <AutomationsFiltersBar
                                draftFilters={draftFilters}
                                setDraftFilters={setDraftFilters}
                                onSearch={handleApplyFilters}
                                onReset={handleResetFilters}
                            />
                        </div>
                    </Collapse>
                </div>

                <div className="dashboard_app_table dashboard_app_table--list-records">
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Status</th>
                                            <th>Entered</th>
                                            <th>Completed</th>
                                            <th>Updated</th>
                                            <th className="col-action" style={{ textAlign: 'right' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : rows.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    No automations found
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((row, index) => {
                                                const statusOpt = findOptionByValue(AUTOMATION_STATUS_CODES, row.status);
                                                return (
                                                    <tr key={index}>
                                                        <td className="message-name">{row.name}</td>
                                                        <td>
                                                            {statusOpt ? (
                                                                <span className={statusOpt.badgeClass}>{statusOpt.label}</span>
                                                            ) : (
                                                                <span>—</span>
                                                            )}
                                                        </td>
                                                        <td>{row.entered_count}</td>
                                                        <td>
                                                            {row.completed_count}
                                                        </td>
                                                        <td>
                                                            {formatDateTime(row.updatedAt)}
                                                        </td>
                                                        <td className="col-action" style={{ textAlign: 'right' }}>
                                                            <button
                                                                type="button"
                                                                className="dropdown-trigger-btn btn btn-link p-0"
                                                                aria-label="Actions"
                                                                onClick={(e) => handleOpenDropdown(e, row.id, row)}
                                                            >
                                                                <BsThreeDotsVertical />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={safePage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Automations pagination"
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {openActionItem &&
                dropdownPosition &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'fixed',
                            left: dropdownPosition.left,
                            top: dropdownPosition.top,
                            zIndex: 10000,
                            minWidth: '220px',
                        }}
                    >
                        <div className="dropdown-menu-subscription">
                            {canAny(["automation.edit"]) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        closeDropdown();
                                        handleOpenAutomation(openActionItem);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    {[3, 5, 6].includes(Number(openActionItem.status))
                                        ? <MdOutlineRemoveRedEye size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                        : <MdEdit size={18} style={{ marginRight: '8px', flexShrink: 0 }} />}
                                    <span style={{ fontSize: '0.875rem' }}>{[3, 5, 6].includes(Number(openActionItem.status)) ? 'View' : 'Edit'}</span>
                                </button>
                            )}
                            {canAny(["automation.edit"]) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        closeDropdown();
                                        handleOpenRenameModal(openActionItem);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <BsPencil size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.875rem' }}>Rename</span>
                                </button>
                            )}
                            {canAny(["automation.edit"]) && (
                                <button
                                    type="button"
                                    disabled={duplicating}
                                    onClick={() => {
                                        closeDropdown();
                                        handleDuplicateAutomation(openActionItem);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <LuCopy size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.875rem' }}>Duplicate</span>
                                </button>
                            )}
                            {canAny(["automation.view"]) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        closeDropdown();
                                        handleOpenAuditLogs(openActionItem);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                >
                                    <BsClockHistory size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.875rem' }}>View Audit Logs</span>
                                </button>
                            )}
                            {canAny(["automation.change_status"]) &&
                                [1, 2, 3, 4].includes(Number(openActionItem.status)) && (
                                    <button
                                        type="button"
                                        disabled={statusUpdating}
                                        onClick={() => {
                                            closeDropdown();
                                            handleToggleStatus(openActionItem);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                                    >
                                        {Number(openActionItem.status) === findOptionByValue(AUTOMATION_STATUS_CODES, 3)?.value ? (
                                            <>
                                                <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                                <span style={{ fontSize: '0.875rem' }}>Pause</span>
                                            </>
                                        ) : (
                                            <>
                                                <MdOutlineCheckCircle size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                                <span style={{ fontSize: '0.875rem' }}>Set Live</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            {canAny(["automation.delete"]) && (
                                    <button
                                        type="button"
                                        className="delete-class"
                                        onClick={() => {
                                            closeDropdown();
                                            handleDeleteAutomation(openActionItem);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', color: 'red' }}
                                    >
                                        <MdDeleteOutline size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                                        <span style={{ fontSize: '0.875rem' }}>Delete</span>
                                    </button>
                                )}
                        </div>
                    </div>,
                    document.body
                )}


            <Modal show={showNewModal} onHide={() => !creating && setShowNewModal(false)} centered className="ns-modal">
                <Modal.Header closeButton>
                    <Modal.Title>New Automation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Automation name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim() && !creating) handleCreate(); }}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn_theme_secondary" onClick={() => setShowNewModal(false)} disabled={creating}>Cancel</button>
                    <button type="button" className="add_app_btn btn_theme_primary" disabled={creating || !newName.trim()} onClick={handleCreate}>
                        <span>{creating ? 'Creating…' : 'Create'}</span>
                    </button>
                </Modal.Footer>
            </Modal>


            <Modal show={showRenameModal} onHide={() => { setShowRenameModal(false); setSelectedAutomation(null); }} centered className="ns-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Rename Automation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Automation name"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        autoFocus
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn_theme_secondary" onClick={() => { setShowRenameModal(false); setSelectedAutomation(null); }} disabled={renaming}>
                        Close
                    </button>
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={handleRenameAutomation} disabled={renaming || !renameValue.trim()}>
                        <span>{renaming ? 'Saving…' : 'Save'}</span>
                    </button>
                </Modal.Footer>
            </Modal>


            <AutomationAuditLogsModal
                show={showAuditModal}
                automationName={selectedAutomation?.name}
                logs={auditLogs}
                loading={auditLoading}
                onClose={() => { setShowAuditModal(false); setSelectedAutomation(null); setAuditLogs([]); }}
            />
        </div>
    );

};

export default AutomationsHome;